(() => {
  if (customElements.get('evolved-iphone-model-guide')) return;
  const normalize = value => String(value || '').trim().replace(/\s+/g, ' ').replace(/[″“”]/g, '"').replace(/\s*\/\s*/g, '/').replace(/(\d+(?:\/\d+)*) Gen\b/g, 'Gen $1').replace(/^(Samsung (?:Galaxy )?S\d+) Plus$/i, '$1+');
  const family = value => /^iPhone\s+(?:\d+[a-z]?|Duo|Air|Ultra|SE|XS|XR|X)\b/i.exec(value)?.[0] || (/iPad/i.test(value) ? 'iPad' : /Samsung|Galaxy/i.test(value) ? 'Samsung Galaxy' : /Watch|\d+\s*mm/i.test(value) ? 'Apple Watch' : value);
  class ModelGuide extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.sectionPicker = this.hasAttribute('data-section-picker');
      this.collectionPicker = this.sectionPicker || this.hasAttribute('data-collection-picker');
      this.choice = this.querySelector('[data-model-choice]');
      this.status = this.querySelector('[data-model-status]');
      this.grid = this.querySelector('[data-model-grid]');
      this.cache = new Map();
      this.choice.addEventListener('change', () => {
        if (this.collectionPicker && !this.sectionPicker) {
          const url = new URL(location.href);
          if (this.choice.value) url.searchParams.set('model', this.choice.value);
          else url.searchParams.delete('model');
          history.replaceState(history.state, '', url);
        }
        this.showModel();
      }, {signal:this.controller.signal});
      if (this.collectionPicker && !this.sectionPicker) window.addEventListener('popstate', () => {
        this.choice.value = normalize(new URLSearchParams(location.search).get('model'));
        this.showModel();
      }, {signal:this.controller.signal});
      this.initialize();
    }
    disconnectedCallback() { this.request?.abort(); this.controller?.abort(); this.restoreSections(); this.started = false; }
    restoreSections() {
      this.browseSections?.forEach(section => section.classList.remove('ev-model-results-hidden'));
      if (!this.sectionPicker) this.closest('.shopify-section')?.querySelector('evolved-collection-links')?.classList.remove('ev-model-results-hidden');
    }
    setCollectionResults(active) {
      if (!this.collectionPicker) return;
      if (!this.browseSections) {
        const ownSection = this.closest('.shopify-section');
        this.browseSections = this.sectionPicker ? [...ownSection.querySelectorAll('[data-model-browse]')] : [...new Set([...document.querySelectorAll('#main [data-ev-product-rail], #main evolved-watch-bands, #main evolved-device-guide')].map(node => node.closest('.shopify-section')).filter(section => section && section !== ownSection))];
      }
      this.browseSections.forEach(section => section.classList.toggle('ev-model-results-hidden', active));
      if (!this.sectionPicker) this.closest('.shopify-section')?.querySelector('evolved-collection-links')?.classList.toggle('ev-model-results-hidden', active);
      this.grid.hidden = !active;
    }
    async documentAt(url, signal) {
      const response = await fetch(url, {credentials:'same-origin', signal, headers:{Accept:'text/html'}});
      if (!response.ok) throw Error('Unable to load products');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }
    async initialize() {
      try {
        let page = JSON.parse(this.querySelector('[data-model-catalogue]').textContent);
        const products = new Map(), visited = new Set();
        while (page) {
          for (const product of page.products) products.set(String(product.id), product);
          if (!page.next || visited.has(page.next)) break;
          visited.add(page.next);
          const url = new URL(page.next, location.href);
          url.searchParams.set('section_id', this.dataset.section);
          const doc = await this.documentAt(url, AbortSignal.timeout(12000));
          const data = doc.querySelector('[data-model-catalogue]');
          if (!data) throw Error('Unable to load the full catalogue');
          page = JSON.parse(data.textContent);
        }
        this.products = [...products.values()];
        const groups = new Map();
        for (const product of this.products) for (const model of product.models) {
          const name = normalize(model.name), group = family(name);
          if (!groups.has(group)) groups.set(group, new Set());
          groups.get(group).add(name);
        }
        let families = [...groups.keys()].sort((a,b) => {
          const number = value => Number(/^iPhone (\d+)/.exec(value)?.[1] || 0);
          return number(b) - number(a);
        });
        const duo = families.findIndex(name => /^iPhone Duo$/i.test(name));
        if (duo >= 0) {
          const [name] = families.splice(duo, 1), eighteen = families.indexOf('iPhone 18');
          families.splice(eighteen >= 0 ? eighteen + 1 : 0, 0, name);
        }
        this.choice.replaceChildren();
        if (this.collectionPicker) {
          const all = document.createElement('option'); all.value = ''; all.textContent = this.dataset.allLabel; this.choice.append(all);
        }
        for (const name of families) {
          const group = document.createElement('optgroup'); group.label = name;
          for (const model of groups.get(name)) {
            const option = document.createElement('option'); option.value = option.textContent = model; group.append(option);
          }
          this.choice.append(group);
        }
        const requested = this.sectionPicker ? '' : normalize(new URLSearchParams(location.search).get('model'));
        if (requested && [...this.choice.options].some(option => option.value === requested)) this.choice.value = requested;
        this.choice.disabled = this.choice.options.length <= (this.collectionPicker ? 1 : 0);
        if (this.choice.disabled) { this.status.textContent = this.collectionPicker ? 'No available models right now. Browse the products below.' : 'No available iPhone cases right now.'; return; }
        await this.showModel();
      } catch (_) { this.status.textContent = this.collectionPicker ? 'Models could not load. Browse the products below or refresh to try again.' : 'Cases could not load. Refresh to try again, or browse all cases above.'; }
    }
    async cardFor(product, model, color, signal) {
      const url = new URL(product.url, location.origin);
      url.searchParams.set('section_id', 'evolved-model-card');
      url.searchParams.set('option_values', [model.id, color].filter(Boolean).join(','));
      const key = url.toString();
      let markup = this.cache.get(key);
      if (!markup) {
        const doc = await this.documentAt(url, signal), response = doc.querySelector('[data-model-response]');
        if (!response) throw Error('Unable to load the selected variant');
        markup = response.outerHTML; this.cache.set(key, markup);
      }
      const response = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-model-response]');
      if (response.dataset.available !== 'true' || normalize(response.dataset.model) !== normalize(model.name)) return null;
      const card = response.querySelector('.ev-guide-card');
      if (!card) return null;
      card.dataset.guideModel = model.name;
      return card;
    }
    async showModel() {
      this.request?.abort(); const request = new AbortController(); this.request = request;
      const chosen = this.choice.value;
      if (this.collectionPicker && !chosen) {
        this.setCollectionResults(false); this.grid.replaceChildren(); this.grid.removeAttribute('aria-busy');
        this.status.textContent = this.dataset.idleLabel; return;
      }
      this.setCollectionResults(true);
      const matches = this.products.flatMap(product => {
        const model = product.models.find(model => normalize(model.name) === chosen);
        return model ? [{product, model}] : [];
      });
      this.status.textContent = 'Finding ' + (this.collectionPicker ? 'products' : 'cases') + ' for ' + chosen + '…';
      this.grid.setAttribute('aria-busy', 'true'); this.grid.replaceChildren();
      let cursor = 0, failures = 0;
      const cards = new Array(matches.length);
      const worker = async () => {
        while (cursor < matches.length && !request.signal.aborted) {
          const index = cursor++, {product, model} = matches[index];
          try { cards[index] = await this.cardFor(product, model, null, request.signal); }
          catch (error) { if (!request.signal.aborted) failures++; }
        }
      };
      await Promise.all(Array.from({length:Math.min(4, matches.length)}, worker));
      if (request.signal.aborted) return;
      const visible = cards.filter(Boolean); this.grid.replaceChildren(...visible); this.grid.removeAttribute('aria-busy');
      const noun = this.collectionPicker ? 'product' : 'case';
      this.status.textContent = failures ? (this.collectionPicker ? 'Some products could not load. Choose All models to browse, or select your model again to retry.' : 'Some cases could not load. Select your model again to retry, or browse all cases above.') : visible.length ? visible.length + ' ' + noun + (visible.length === 1 ? ' fits ' : 's fit ') + chosen + '.' : 'No available ' + noun + 's for ' + chosen + ' right now. Try another model.';
    }
  }
  customElements.define('evolved-iphone-model-guide', ModelGuide);
})();
